import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setPageDimensions } from '../redux/slices/generalDocumentReducer';

/**
 * PdfViewer — Main PDF page viewer for the editor canvas area.
 *
 * CHANGED FROM: react-pdf-js (unmaintained; loaded the full PDF per instance)
 * CHANGED TO:   Direct canvas rendering using a shared pdfDoc passed via props.
 *
 * The pdfDoc is now owned by homePage via usePdfDocument() and shared with
 * all consumers by passing `getPage` as a prop. This means the PDF is parsed
 * and decoded exactly ONCE, regardless of how many viewers/thumbnails exist.
 *
 * @param {object}   props
 * @param {Function} props.getPage       - From usePdfDocument: (pageNumber) => Promise<PDFPageProxy>
 * @param {number}   props.activePage    - 0-indexed current page number
 * @param {Function} props.setPDFLoading - Callback to set loading state in parent
 * @param {Function} props.setPageCount  - Callback to report total page count
 * @param {number}   props.numPages      - Total page count (from usePdfDocument)
 * @param {number|string} props.scale    - Render scale (default 1)
 */
const PdfViewer = ({
  getPage,
  activePage,
  setPDFLoading,
  setPageCount,
  numPages,
  scale = 1,
}) => {
  const dispatch = useDispatch();
  const canvasRef = useRef(null);
  const renderTaskRef = useRef(null);
  const [renderError, setRenderError] = useState(false);

  // Report page count upward once we know it
  useEffect(() => {
    if (numPages && numPages > 0) {
      setPageCount(numPages);
      setPDFLoading(false);
    }
  }, [numPages, setPageCount, setPDFLoading]);

  // Render the active page on the canvas whenever activePage or getPage changes
  useEffect(() => {
    if (!getPage || numPages === 0) return;

    const pageNumber = (activePage ?? 0) + 1; // Convert 0-indexed → 1-indexed
    if (pageNumber < 1 || pageNumber > numPages) return;

    let cancelled = false;
    setRenderError(false);

    const renderPage = async () => {
      try {
        setPDFLoading(true);

        // Cancel any in-flight render from a previous page navigation
        if (renderTaskRef.current) {
          try { await renderTaskRef.current.cancel(); } catch (_) { /* ignore */ }
          renderTaskRef.current = null;
        }

        const page = await getPage(pageNumber);
        if (cancelled) return;

        const numericScale = typeof scale === 'string' ? parseFloat(scale) : scale;
        const viewport = page.getViewport({ scale: numericScale || 1 });

        // Report page dimensions to Redux
        dispatch(setPageDimensions({ width: viewport.width, height: viewport.height }));

        const canvas = canvasRef.current;
        if (!canvas || cancelled) return;

        canvas.width = viewport.width;
        canvas.height = viewport.height;

        const ctx = canvas.getContext('2d');

        // Yield to the browser before starting the heavy canvas draw.
        // This keeps the UI interactive during page navigation.
        await new Promise((resolve) => requestAnimationFrame(resolve));
        if (cancelled) return;

        const renderTask = page.render({ canvasContext: ctx, viewport });
        renderTaskRef.current = renderTask;

        await renderTask.promise;
        if (!cancelled) {
          setPDFLoading(false);
        }
      } catch (err) {
        if (err?.name !== 'RenderingCancelledException') {
          console.error('[PdfViewer] Render error:', err);
          if (!cancelled) {
            setRenderError(true);
            setPDFLoading(false);
          }
        }
      }
    };

    renderPage();

    return () => {
      cancelled = true;
      renderTaskRef.current?.cancel?.();
    };
  }, [activePage, getPage, numPages, scale, setPDFLoading]);

  if (renderError) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, color: '#c00' }}>
        Failed to render page. Please refresh.
      </div>
    );
  }

  return (
    <div className="pdff">
      <canvas ref={canvasRef} style={{ display: 'block' }} />
    </div>
  );
};

export default PdfViewer;
